import React, { useEffect, useState } from 'react';
import { useParamsContext } from '../../../contexts/ParamsContext';
import PriceInput from '../../../components/inputs/Price';
import DurationInput from '../../../components/inputs/Duration';
import { ConfirmDialog } from '../../../components/ConfirmDialog';
import { ServicosService } from '../Service';
import { useAppContext } from '../../../contexts/AppContext';
import { useServicosContext } from '../Context';
import SelectAtendentes from '../../../components/inputs/SelectAtendentes';
import { SettingsRow, SettingsSection } from '../../configuracoes/estabelecimento/Form/SettingsGroup';
import FormServicoImages from './Images';
import { Plus, Scissors } from 'lucide-react';

const service = new ServicosService();

const weekDays = [
    { value: 0, label: 'Dom', name: 'Domingo' },
    { value: 1, label: 'Seg', name: 'Segunda-feira' },
    { value: 2, label: 'Ter', name: 'Terça-feira' },
    { value: 3, label: 'Qua', name: 'Quarta-feira' },
    { value: 4, label: 'Qui', name: 'Quinta-feira' },
    { value: 5, label: 'Sex', name: 'Sexta-feira' },
    { value: 6, label: 'Sáb', name: 'Sábado' },
];

const createRuleId = () => `rule_${Date.now()}_${Math.random().toString(16).slice(2)}`;

const createEmptyRule = () => ({
    id: createRuleId(),
    daysOfWeek: [],
    attendantsIds: [],
    price: '',
    active: true,
});

export default function ServicosForm() {
    const { clear, id } = useParamsContext();
    const [loading, setLoading] = useState(false);
    const [openDelete, setOpenDelete] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const { reloadData, data, loading: loadingList } = useServicosContext();
    const { successSnackbar, errorSnackbar } = useAppContext();

    const [name, setName] = useState('');
    const [price, setPrice] = useState<string | number>('');
    const [imagesIds, setImagesIds] = useState<string[]>([]);
    const [duration, setDuration] = useState(30);
    const [description, setDescription] = useState('');
    const [principalImage, setPrincipalImage] = useState('');
    const [attendantsIds, setAttendantsIds] = useState<string[]>([]);
    const [allowClientScheduling, setAllowClientScheduling] = useState(true);
    const [requiresSubscription, setRequiresSubscription] = useState(false);
    const [priceRules, setPriceRules] = useState<any[]>([]);
    const doc = data?.find((e) => e._id === id);
    const docId = doc?._id;

    // Preenche ao abrir: recarregar a lista nao pode apagar o que foi digitado.
    useEffect(() => {
        if (!docId) return;

        setName(doc.name || '');
        setPrice(doc.price ?? '');
        setDuration(doc.duration || 0);
        setImagesIds(doc.imagesIds || []);
        setDescription(doc.description || '');
        setAttendantsIds(doc.attendantsIds || []);
        setPrincipalImage(doc.principalImage || '');
        setAllowClientScheduling(doc.allowClientScheduling !== false);
        setRequiresSubscription(Boolean(doc.requiresSubscription));
        setPriceRules(
            (Array.isArray(doc.priceRules) ? doc.priceRules : []).map((rule: any) => ({
                id: createRuleId(),
                daysOfWeek: Array.isArray(rule?.daysOfWeek) ? rule.daysOfWeek : [],
                attendantsIds: Array.isArray(rule?.attendantsIds) ? rule.attendantsIds : [],
                price: rule?.price ?? '',
                active: rule?.active !== false,
                createdAt: rule?.createdAt,
                createdBy: rule?.createdBy,
            })),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docId]);

    const submit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (duration <= 0) {
            errorSnackbar('Informe a duração do serviço.');
            return;
        }

        const normalizedRules = priceRules.map((rule) => ({
            daysOfWeek: Array.from(new Set<number>(rule.daysOfWeek || [])).sort((a, b) => a - b),
            attendantsIds: Array.from(new Set<string>(rule.attendantsIds || [])).sort(),
            price: rule.price === '' ? NaN : Number(rule.price),
            active: rule.active !== false,
            createdAt: rule.createdAt,
            createdBy: rule.createdBy,
        }));

        if (normalizedRules.some((rule) => !rule.daysOfWeek.length || !Number.isFinite(rule.price))) {
            errorSnackbar('Informe dias e preço em todos os preços especiais.');
            return;
        }

        const keys = new Set<string>();
        const duplicates = normalizedRules.some((rule) => {
            const key = `${rule.daysOfWeek.join(',')}|${rule.attendantsIds.length ? rule.attendantsIds.join(',') : 'ALL'}`;
            if (keys.has(key)) return true;
            keys.add(key);
            return false;
        });

        if (duplicates) {
            errorSnackbar('Há preços especiais repetidos para os mesmos dias e atendentes.');
            return;
        }

        const payload = {
            name: name.trim(),
            duration,
            attendantsIds,
            description,
            price,
            imagesIds,
            principalImage,
            allowClientScheduling,
            requiresSubscription,
            priceRules: normalizedRules,
        };

        try {
            setLoading(true);

            if (id) {
                await service.update(id, payload);
                successSnackbar('Serviço editado com sucesso!');
            } else {
                await service.create(payload);
                successSnackbar('Serviço criado com sucesso!');
            }

            await reloadData();
            clear();
        } catch (e: any) {
            console.log(e);
            errorSnackbar(e?.response?.data?.message || 'Ocorreu um erro ao salvar o serviço.');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        try {
            setDeleting(true);
            await service.delete(id);
            await reloadData();
            successSnackbar('Serviço removido.');
            clear();
        } catch (e: any) {
            console.error(e);
            errorSnackbar(e?.response?.data?.message || 'Não foi possível remover o serviço.');
            setDeleting(false);
            setOpenDelete(false);
        }
    };

    const updateRule = (index: number, patch: any) => {
        setPriceRules((prev) => prev.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));
    };

    const toggleRuleDay = (index: number, dayValue: number) => {
        const days: number[] = priceRules[index]?.daysOfWeek || [];

        updateRule(index, {
            daysOfWeek: days.includes(dayValue) ? days.filter((day) => day !== dayValue) : [...days, dayValue],
        });
    };

    if (id && !doc) {
        return loadingList ? (
            <div className="space-y-3 px-2" aria-busy="true">
                <div className="sf-skeleton h-40" />
                <div className="sf-skeleton h-24" />
            </div>
        ) : (
            <div className="sf-state">
                <Scissors aria-hidden />
                <h2>Serviço não encontrado</h2>
                <button type="button" className="sf-link" onClick={clear}>
                    Voltar para serviços
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={submit} className="estab-settings sf-page px-2">
            <div className="grid gap-6 xl:grid-cols-2 xl:items-start">
                <div className="space-y-6">
                    <SettingsSection title="Serviço">
                        <SettingsRow label="Nome" htmlFor="servico-nome">
                            <input
                                id="servico-nome"
                                type="text"
                                className="input estab-field"
                                placeholder="Como o cliente vai ver"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                            />
                        </SettingsRow>
                        <SettingsRow label="Duração" htmlFor="servico-duracao">
                            <DurationInput
                                id="servico-duracao"
                                label=""
                                min="00:10"
                                value={duration}
                                onChange={setDuration}
                                placeholder="Duração do serviço"
                                inputClassName="input estab-field"
                            />
                        </SettingsRow>
                        <SettingsRow label="Preço base">
                            <PriceInput
                                label=""
                                className="estab-field w-full"
                                value={price}
                                onChange={(e) => setPrice(e.target.value)}
                                placeholder="R$ 0,00"
                                required
                            />
                        </SettingsRow>
                        <SettingsRow label="Descrição" htmlFor="servico-descricao" stacked>
                            <textarea
                                id="servico-descricao"
                                className="textarea estab-field !ml-0 !min-h-[5rem] !w-full"
                                placeholder="O que está incluído"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            ></textarea>
                        </SettingsRow>
                    </SettingsSection>

                    <SettingsSection title="Atendentes">
                        <SettingsRow stacked>
                            <SelectAtendentes
                                label=""
                                value={attendantsIds}
                                setValue={setAttendantsIds}
                                name="atendentes"
                                menuOverlay
                            />
                        </SettingsRow>
                    </SettingsSection>

                    <SettingsSection title="Agendamento">
                        <SettingsRow label="Cliente pode agendar" htmlFor="servico-cliente" inline>
                            <input
                                id="servico-cliente"
                                type="checkbox"
                                className="toggle toggle-success"
                                checked={allowClientScheduling}
                                onChange={(e) => setAllowClientScheduling(e.target.checked)}
                            />
                        </SettingsRow>
                        <SettingsRow
                            label="Exclusivo para assinatura"
                            htmlFor="servico-assinatura"
                            description="No autoagendamento, só aparece para quem tem assinatura ativa com este serviço."
                            inline
                        >
                            <input
                                id="servico-assinatura"
                                type="checkbox"
                                className="toggle toggle-success"
                                checked={requiresSubscription}
                                onChange={(e) => setRequiresSubscription(e.target.checked)}
                            />
                        </SettingsRow>
                    </SettingsSection>
                </div>

                <div className="space-y-6">
                    <section>
                        <div className="flex items-end justify-between gap-2">
                            <h2 className="sf-group-title">Preços especiais</h2>
                            <button
                                type="button"
                                className="sf-link mb-1.5"
                                onClick={() => setPriceRules((prev) => [...prev, createEmptyRule()])}
                            >
                                <Plus aria-hidden />
                                Adicionar
                            </button>
                        </div>

                        {priceRules.length === 0 ? (
                            <p className="px-1 text-xs text-base-content/55">
                                Sem preços especiais, vale sempre o preço base. Use para cobrar diferente por dia da semana ou atendente.
                            </p>
                        ) : (
                            <div className="space-y-3">
                                {priceRules.map((rule, index) => (
                                    <div key={rule.id} className="estab-group">
                                        <div className="sf-days p-3" role="group" aria-label="Dias da semana">
                                            {weekDays.map((day) => (
                                                <button
                                                    type="button"
                                                    key={day.value}
                                                    className="sf-day"
                                                    aria-pressed={rule.daysOfWeek?.includes(day.value)}
                                                    aria-label={day.name}
                                                    onClick={() => toggleRuleDay(index, day.value)}
                                                >
                                                    {day.label}
                                                </button>
                                            ))}
                                        </div>
                                        <SettingsRow stacked>
                                            <SelectAtendentes
                                                label=""
                                                placeholder="Todos os atendentes"
                                                value={rule.attendantsIds || []}
                                                setValue={(value) => updateRule(index, { attendantsIds: value })}
                                                name={`atendentes_rule_${index}`}
                                                menuOverlay
                                            />
                                        </SettingsRow>
                                        <SettingsRow label="Preço">
                                            <PriceInput
                                                label=""
                                                className="estab-field w-full"
                                                value={rule.price}
                                                onChange={(e) => updateRule(index, { price: e.target.value })}
                                                placeholder="R$ 0,00"
                                            />
                                        </SettingsRow>
                                        <SettingsRow label="Ativo" htmlFor={`regra-ativa-${rule.id}`} inline>
                                            <input
                                                id={`regra-ativa-${rule.id}`}
                                                type="checkbox"
                                                className="toggle toggle-success"
                                                checked={rule.active !== false}
                                                onChange={(e) => updateRule(index, { active: e.target.checked })}
                                            />
                                        </SettingsRow>
                                        <button
                                            type="button"
                                            className="sf-destructive-row"
                                            onClick={() => setPriceRules((prev) => prev.filter((_, i) => i !== index))}
                                        >
                                            Remover preço especial
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </section>

                    <SettingsSection title="Imagens">
                        <FormServicoImages value={imagesIds} setValue={setImagesIds} disabled={loading} />
                    </SettingsSection>

                    {!!id && (
                        <div className="sf-group">
                            <button type="button" className="sf-destructive-row" onClick={() => setOpenDelete(true)}>
                                Remover serviço
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="estab-savebar mt-6 gap-2">
                <button
                    type="button"
                    className="btn btn-ghost flex-1 sm:flex-none sm:min-w-[8rem]"
                    disabled={loading}
                    onClick={clear}
                >
                    Cancelar
                </button>
                <button
                    type="submit"
                    className="btn btn-primary flex-1 sm:flex-none sm:min-w-[11rem]"
                    disabled={loading}
                >
                    {loading && <span className="loading loading-spinner loading-sm"></span>}
                    {id ? 'Salvar alterações' : 'Criar serviço'}
                </button>
            </div>

            {openDelete && (
                <ConfirmDialog
                    title="Remover serviço?"
                    confirmLabel="Remover"
                    loading={deleting}
                    onConfirm={handleDelete}
                    onClose={() => setOpenDelete(false)}
                >
                    {name || 'O serviço'} sai da lista de serviços. Agendamentos já feitos não são alterados. Para apenas
                    pausar, desative o serviço na lista.
                </ConfirmDialog>
            )}
        </form>
    );
}
